// Стандартные действия для разрешений
export const PERMISSION_ACTIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  SEND: 'send', // для notifications
  ALL: '*', // для суперпользователя
} as const;

// Доступные сервисы
export const SERVICES = {
  AUTH: 'auth',
  ANALYTICS: 'analytics', 
  RETURNS: 'returns',
  NOTIFICATIONS: 'notifications',
} as const;

// Специальные разрешения для auth сервиса
export const AUTH_RESOURCES = {
  USERS: 'users',
  ROLES: 'roles',
} as const;

// Специальные разрешения для returns сервиса
export const RETURNS_RESOURCES = {
  ORDERS: 'orders',
} as const;

// Стандартные действия для большинства сервисов
export const STANDARD_ACTIONS = [
  PERMISSION_ACTIONS.READ,
  PERMISSION_ACTIONS.WRITE,
  PERMISSION_ACTIONS.DELETE,
] as const;

// Специальные действия для конкретных сервисов
export const SERVICE_SPECIFIC_ACTIONS = {
  [SERVICES.NOTIFICATIONS]: [
    ...STANDARD_ACTIONS,
    PERMISSION_ACTIONS.SEND,
  ],
} as const;

// Ресурсы для конкретных сервисов (если нужны более детальные разрешения)
export const SERVICE_RESOURCES = {
  [SERVICES.AUTH]: [
    AUTH_RESOURCES.USERS,
    AUTH_RESOURCES.ROLES,
  ],
  [SERVICES.RETURNS]: [
    RETURNS_RESOURCES.ORDERS,
  ],
} as const;

// Получение доступных действий для сервиса
export const getActionsForService = (serviceName: string): readonly string[] => {
  return SERVICE_SPECIFIC_ACTIONS[serviceName as keyof typeof SERVICE_SPECIFIC_ACTIONS] || STANDARD_ACTIONS;
};

// Получение ресурсов для сервиса (если есть)
export const getResourcesForService = (serviceName: string): readonly string[] | null => {
  return SERVICE_RESOURCES[serviceName as keyof typeof SERVICE_RESOURCES] || null;
};

// Формирование полного разрешения
export const buildPermission = (service: string, resource: string | null, action: string): string => {
  if (resource) {
    return `${service}:${resource}:${action}`;
  }
  return `${service}:${action}`;
};

// Глобальные разрешения (не привязанные к сервису)
export const GLOBAL_PERMISSIONS = [
  PERMISSION_ACTIONS.ALL, // суперпользователь
] as const;

// Описания действий
export const ACTION_DESCRIPTIONS = {
  [PERMISSION_ACTIONS.READ]: 'Просмотр',
  [PERMISSION_ACTIONS.WRITE]: 'Создание и редактирование',
  [PERMISSION_ACTIONS.DELETE]: 'Удаление',
  [PERMISSION_ACTIONS.SEND]: 'Отправка',
  [PERMISSION_ACTIONS.ALL]: 'Все разрешения (суперпользователь)',
} as const;

// Описания сервисов
export const SERVICE_DESCRIPTIONS = {
  [SERVICES.AUTH]: 'Сервис авторизации',
  [SERVICES.ANALYTICS]: 'Сервис аналитики',
  [SERVICES.RETURNS]: 'Сервис возвратов',
  [SERVICES.NOTIFICATIONS]: 'Сервис уведомлений',
} as const;

// Описания ресурсов
export const RESOURCE_DESCRIPTIONS = {
  [AUTH_RESOURCES.USERS]: 'Пользователи',
  [AUTH_RESOURCES.ROLES]: 'Роли',
  [RETURNS_RESOURCES.ORDERS]: 'Заказы',
} as const;

// Получение описания для разрешения
export const getPermissionDescription = (permission: string): string => {
  if (permission === PERMISSION_ACTIONS.ALL) {
    return ACTION_DESCRIPTIONS[PERMISSION_ACTIONS.ALL];
  }
  
  const parts = permission.split(':');
  if (parts.length === 2) {
    // service:action
    const [service, action] = parts;
    const serviceDesc = SERVICE_DESCRIPTIONS[service as keyof typeof SERVICE_DESCRIPTIONS] || service;
    const actionDesc = ACTION_DESCRIPTIONS[action as keyof typeof ACTION_DESCRIPTIONS] || action;
    return `${serviceDesc}: ${actionDesc}`;
  } else if (parts.length === 3) {
    // service:resource:action
    const [service, resource, action] = parts;
    const serviceDesc = SERVICE_DESCRIPTIONS[service as keyof typeof SERVICE_DESCRIPTIONS] || service;
    const resourceDesc = RESOURCE_DESCRIPTIONS[resource as keyof typeof RESOURCE_DESCRIPTIONS] || resource;
    const actionDesc = ACTION_DESCRIPTIONS[action as keyof typeof ACTION_DESCRIPTIONS] || action;
    return `${serviceDesc} - ${resourceDesc}: ${actionDesc}`;
  }
  
  return permission;
};